import { useEffect, useMemo, useState } from "react";
import {
  collection,
  query,
  orderBy,
  startAt,
  endAt,
  limit,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

const NP_API_URL = "https://api.novaposhta.ua/v2.0/json/";

function normalizePhone(s = "") {
  return String(s).replace(/\D/g, "");
}

export default function BusinessAddressesManager({ uid, novaPoshtaKey }) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // форма додавання
  const [fio, setFio] = useState("");
  const [phone, setPhone] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [cityChoice, setCityChoice] = useState(null);
  const [citiesList, setCitiesList] = useState([]);
  const [deptChoice, setDeptChoice] = useState(null);
  const [deptsList, setDeptsList] = useState([]);

  const canSearch = useMemo(() => search.trim().length >= 1, [search]);

  const fetchAddresses = async () => {
    if (!uid) return;
    setLoading(true);
    const ref = collection(db, `users/${uid}/deliveryAddresses`);
    const term = search.trim().toLowerCase();

    try {
      let qList = [];
      if (term) {
        // 3 окремі префіксні пошуки — по ПІБ, телефону (цифри), місту
        const qByFio = query(
          ref,
          orderBy("fioLC"),
          startAt(term),
          endAt(term + "\uf8ff"),
          limit(20),
        );
        const qByPhone = query(
          ref,
          orderBy("phoneNorm"),
          startAt(normalizePhone(term)),
          endAt(normalizePhone(term) + "\uf8ff"),
          limit(20),
        );
        const qByCity = query(
          ref,
          orderBy("cityLC"),
          startAt(term),
          endAt(term + "\uf8ff"),
          limit(20),
        );
        qList = [qByFio, qByPhone, qByCity];
      } else {
        // без фільтра — перші 20 для швидкого старту
        qList = [query(ref, orderBy("createdAt", "desc"), limit(20))];
      }

      const all = [];
      for (const qx of qList) {
        const snap = await getDocs(qx);
        snap.forEach((d) => all.push({ id: d.id, ...d.data() }));
      }
      // де-дуп
      const dedup = Object.values(
        all.reduce((acc, r) => {
          acc[r.id] = r;
          return acc;
        }, {}),
      );
      setRows(dedup);
    } catch (e) {
      console.error("Помилка пошуку адрес:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    const id = setTimeout(fetchAddresses, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ---- НП: пошук міст ----
  const onCityInput = async (val) => {
    setCityInput(val);
    setCityChoice(null);
    setDeptsList([]);
    setDeptChoice(null);
    if (!novaPoshtaKey || !val.trim()) return setCitiesList([]);

    try {
      const body = {
        apiKey: novaPoshtaKey,
        modelName: "Address",
        calledMethod: "getCities",
        methodProperties: { FindByString: val },
      };
      const res = await fetch(NP_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setCitiesList(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      console.error("НП: помилка getCities", e);
    }
  };

  // ---- НП: відділення ----
  const loadDepts = async (cityRef) => {
    if (!novaPoshtaKey || !cityRef) return setDeptsList([]);
    try {
      const body = {
        apiKey: novaPoshtaKey,
        modelName: "AddressGeneral",
        calledMethod: "getWarehouses",
        methodProperties: { CityRef: cityRef, Page: "1", Limit: "500" },
      };
      const res = await fetch(NP_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setDeptsList(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      console.error("НП: помилка getWarehouses", e);
      setDeptsList([]);
    }
  };

  const onPickCity = async (city) => {
    setCityChoice(city);
    setCityInput(city?.Description || "");
    setCitiesList([]);
    await loadDepts(city?.Ref);
  };

  const onSaveAddress = async () => {
    if (!uid) return;
    const fioTrim = fio.trim();
    const phoneNorm = normalizePhone(phone);
    const dept = deptChoice?.Description || "";
    const cityName = cityChoice?.Description || cityInput.trim();
    const area = cityChoice?.AreaDescription || "";
    if (!fioTrim || !phoneNorm || !cityName || !dept)
      return alert("Заповніть ПІБ, телефон, місто і відділення НП.");

    try {
      const ref = collection(db, `users/${uid}/deliveryAddresses`);
      const payload = {
        fio: fioTrim,
        phone: phoneNorm,
        city: cityName,
        area,
        npWarehouse: dept,
        cityRef: cityChoice?.Ref || null,
        warehouseRef: deptChoice?.Ref || null,

        // поля для пошуку
        fioLC: fioTrim.toLowerCase(),
        phoneNorm,
        cityLC: cityName.toLowerCase(),

        createdAt: serverTimestamp(),
      };
      await addDoc(ref, payload);
      // очистити форму та оновити список
      setFio("");
      setPhone("");
      setCityInput("");
      setCityChoice(null);
      setDeptChoice(null);
      setDeptsList([]);
      await fetchAddresses();
    } catch (e) {
      console.error("Помилка збереження адреси:", e);
      alert("Не вдалося зберегти адресу. Спробуйте ще раз.");
    }
  };

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {/* Пошук існуючих адрес */}
      <div style={{ display: "grid", gap: 8 }}>
        <label style={{ fontWeight: 600 }}>
          Пошук адреси (ПІБ / телефон / населений пункт)
        </label>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Почніть вводити..."
          style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}
        />
        {loading ? (
          <div>Завантаження…</div>
        ) : (
          <div
            style={{
              maxHeight: 260,
              overflow: "auto",
              border: "1px solid #eee",
              borderRadius: 8,
            }}
          >
            {rows.length === 0 ? (
              <div style={{ padding: 10, color: "#777" }}>
                Нічого не знайдено
              </div>
            ) : (
              <table style={{ width: "100%", fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: "left", background: "#fafafa" }}>
                    <th style={{ padding: 8 }}>ПІБ</th>
                    <th style={{ padding: 8 }}>Телефон</th>
                    <th style={{ padding: 8 }}>Населений пункт / Область</th>
                    <th style={{ padding: 8 }}>Відділення НП</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ padding: 8 }}>{r.fio}</td>
                      <td style={{ padding: 8 }}>{r.phone}</td>
                      <td style={{ padding: 8 }}>
                        {r.city}
                        {r.area ? `, ${r.area}` : ""}
                      </td>
                      <td style={{ padding: 8 }}>{r.npWarehouse}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Додавання нової адреси */}
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: "1px dashed #ddd",
          display: "grid",
          gap: 8,
        }}
      >
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <label style={{ fontWeight: 600 }}>ПІБ отримувача</label>
            <input
              type="text"
              value={fio}
              onChange={(e) => setFio(e.target.value)}
              style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}
            />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <label style={{ fontWeight: 600 }}>Телефон отримувача</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="380..."
              style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}
            />
          </div>
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          <label style={{ fontWeight: 600 }}>Місто (НП)</label>
          <input
            type="text"
            value={cityInput}
            onChange={(e) => onCityInput(e.target.value)}
            placeholder="Почніть вводити місто..."
            style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}
          />
          {Array.isArray(citiesList) && citiesList.length > 0 && (
            <div
              style={{
                maxHeight: 180,
                overflow: "auto",
                border: "1px solid #eee",
                borderRadius: 8,
              }}
            >
              {citiesList.map((c) => (
                <div
                  key={c.Ref}
                  onClick={() => onPickCity(c)}
                  style={{ padding: 8, cursor: "pointer" }}
                >
                  {c.SettlementTypeDescription}, {c.Description},{" "}
                  {c.AreaDescription}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          <label style={{ fontWeight: 600 }}>Відділення НП</label>
          <select
            value={deptChoice?.Ref || ""}
            onChange={(e) => {
              const ref = e.target.value;
              const obj = deptsList.find((d) => d.Ref === ref) || null;
              setDeptChoice(obj);
            }}
            disabled={!cityChoice?.Ref}
            style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}
          >
            <option value="">Оберіть відділення</option>
            {deptsList.map((d) => (
              <option key={d.Ref} value={d.Ref}>
                {d.Description}
              </option>
            ))}
          </select>
        </div>

        <div>
          <button
            onClick={onSaveAddress}
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#3e51c1",
              color: "#fff",
              border: 0,
            }}
          >
            Додати адресу
          </button>
        </div>
      </div>
    </div>
  );
}
